import type { LucideIcon } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { cn } from "utils";

export interface PageTab<Key extends string> {
  key: Key;
  label: string;
  icon: LucideIcon;
  /** A count shown beside the label, such as open requests. Hidden at 0. */
  badge?: number;
  /** What the count is, read out after it, e.g. "open requests". */
  badgeLabel?: string;
  /**
   * The count may be short because part of it could not be read. Shown as
   * "3+", or "!" when nothing was counted, never hidden.
   */
  badgeIncomplete?: boolean;
}

export function PageTabs<Key extends string>({
  tabs,
  activeTab,
  onTabChange,
  idPrefix,
  testId,
  className,
}: {
  tabs: readonly PageTab<Key>[];
  activeTab: Key;
  onTabChange: (tab: Key) => void;
  idPrefix?: string;
  testId?: string;
  className?: string;
}) {
  const generatedId = useId();
  const tabsId =
    idPrefix ?? testId ?? `page-tabs-${generatedId.replace(/:/g, "")}`;
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const stripRef = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  // Which edges have tabs behind them. Without this the strip scrolls and says
  // nothing about it: twelve admin tabs need ~1200px and get 1144 at 1440, so
  // the last two sit off the edge with no scrollbar, no arrow and no hint.
  const measureEdges = useCallback(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const max = strip.scrollWidth - strip.clientWidth;
    setEdges({
      start: strip.scrollLeft > 1,
      end: max > 1 && strip.scrollLeft < max - 1,
    });
  }, []);

  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    measureEdges();
    const observer = new ResizeObserver(measureEdges);
    observer.observe(strip);
    return () => observer.disconnect();
  }, [measureEdges, tabs.length]);

  // A selected tab sitting off the edge has to be brought into view, or the
  // selected tab is the one the reader cannot see.
  //
  // Two things here are deliberate and both were bugs first.
  //
  // It moves `scrollLeft` on the strip. `scrollIntoView` walks EVERY
  // scrollable ancestor, the document included, so a reader partway down the
  // agenda was pulled back up to the tab strip.
  //
  // It depends on the tab KEYS rather than the `tabs` array. EventExperience
  // builds that array fresh on each render and re-renders on each Firestore
  // snapshot, so the array identity changes constantly while the tabs do not.
  const tabKeys = tabs.map((tab) => tab.key).join("\u0000");
  useEffect(() => {
    const strip = stripRef.current;
    const selected = strip?.querySelector<HTMLElement>(
      '[aria-selected="true"]',
    );
    if (!strip || !selected) return;

    const stripBox = strip.getBoundingClientRect();
    const tabBox = selected.getBoundingClientRect();
    if (tabBox.left < stripBox.left) {
      strip.scrollLeft -= stripBox.left - tabBox.left;
    } else if (tabBox.right > stripBox.right) {
      strip.scrollLeft += tabBox.right - stripBox.right;
    }
    measureEdges();
  }, [activeTab, tabKeys, measureEdges]);

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    let targetIndex: number | undefined;

    switch (event.key) {
      case "ArrowLeft":
        targetIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case "ArrowRight":
        targetIndex = (index + 1) % tabs.length;
        break;
      case "Home":
        targetIndex = 0;
        break;
      case "End":
        targetIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const target = tabs[targetIndex];
    if (!target) return;
    onTabChange(target.key);
    tabRefs.current[targetIndex]?.focus();
  };

  return (
    // The wrapper carries the rule and the edge fades; the strip inside it is
    // what scrolls. Fading the scroller itself would fade the rule with it.
    <div className={cn("relative mb-8 border-b border-border", className)}>
      <div
        ref={stripRef}
        role="tablist"
        onScroll={measureEdges}
        className="-mx-1 flex gap-1 overflow-x-auto scroll-px-4 pb-px [scrollbar-width:thin]"
        data-testid={testId}
      >
        {tabs.map((tab, index) => {
          const selected = activeTab === tab.key;

          return (
            <button
              key={tab.key}
              ref={(element) => {
                tabRefs.current[index] = element;
              }}
              id={pageTabId(tabsId, tab.key)}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={pageTabPanelId(tabsId, tab.key)}
              tabIndex={selected ? 0 : -1}
              onClick={() => onTabChange(tab.key)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              className={cn(
                "flex min-h-11 shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                selected
                  ? "border-lit text-foreground"
                  : "border-transparent text-muted-foreground hover:border-border hover:text-foreground",
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.badge !== undefined &&
                (tab.badge > 0 || tab.badgeIncomplete) && (
                  <span
                    className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-status-danger px-1.5 text-xs font-bold leading-none text-hb-on-orange"
                    data-testid={`${tabsId}-${tab.key}-badge`}
                  >
                    {tab.badge > 99
                      ? "99+"
                      : tab.badgeIncomplete
                        ? tab.badge > 0
                          ? `${tab.badge}+`
                          : "!"
                        : tab.badge}
                    {tab.badgeLabel && (
                      <span className="sr-only"> {tab.badgeLabel}</span>
                    )}
                  </span>
                )}
            </button>
          );
        })}
      </div>

      <TabEdgeFade side="left" visible={edges.start} />
      <TabEdgeFade side="right" visible={edges.end} />
    </div>
  );
}

/** Says "there are tabs behind this edge". Decoration: the strip is the
 *  scroller, and arrow keys already reach every tab. */
function TabEdgeFade({
  side,
  visible,
}: {
  side: "left" | "right";
  visible: boolean;
}) {
  return (
    <span
      aria-hidden
      data-testid={`page-tabs-fade-${side}`}
      data-visible={visible}
      className={cn(
        "pointer-events-none absolute bottom-px top-0 w-10 transition-opacity",
        side === "left"
          ? "left-0 bg-gradient-to-r from-background to-transparent"
          : "right-0 bg-gradient-to-l from-background to-transparent",
        visible ? "opacity-100" : "opacity-0",
      )}
    />
  );
}

export function PageTabPanel<Key extends string>({
  idPrefix,
  tabKey,
  active,
  children,
  className,
}: {
  idPrefix: string;
  tabKey: Key;
  active: boolean;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      id={pageTabPanelId(idPrefix, tabKey)}
      role="tabpanel"
      aria-labelledby={pageTabId(idPrefix, tabKey)}
      hidden={!active}
      className={className}
    >
      {children}
    </div>
  );
}

/** The id of a tab's button, for moving focus to it. */
export function pageTabId(idPrefix: string, tabKey: string) {
  return `${idPrefix}-${tabKey}-tab`;
}

function pageTabPanelId(idPrefix: string, tabKey: string) {
  return `${idPrefix}-${tabKey}-panel`;
}
